/**
 * §27 — Panelin auth kararlarının SAF çekirdeği.
 *
 * NEDEN ayrı dosya: rol görünürlüğü ("davet formunu kim görür"), 401 yönlendirme
 * kararı ve parola politikası React'e, fetch'e ve react-query'ye bağlı OLMAYAN
 * kurallardır. Burada saf fonksiyon olarak durunca birim testte doğrudan koşulurlar
 * (`auth.test.ts`) ve sayfa bileşenleri yalnız "çiz" işini yapar.
 *
 * Sunucu bu kararların TEKRARINI kendi tarafında yapar (§27.5 preHandler). Buradaki
 * kontroller GÜVENLİK DEĞİL, kullanıcıyı kesin 403 alacağı bir formla karşılaştırmama
 * nezaketidir — bu dosyadaki hiçbir fonksiyon yetkiyi "verir" gibi okunmamalıdır.
 */

/* -------------------------------------------------------------------------- */
/* Roller (§27.1)                                                              */
/* -------------------------------------------------------------------------- */

/** §27.1 — tam sıralı küme: viewer < editor < owner. */
export type Role = 'viewer' | 'editor' | 'owner';

/** Seçici/etiket listelerinde sıralı gezinmek için (davet formundaki `<select>`). */
export const ROLES: readonly Role[] = ['viewer', 'editor', 'owner'] as const;

/** §27.5 — rol karşılaştırması sayısal sıraya indirgenir. */
export const ROLE_ORDER: Record<Role, number> = { viewer: 0, editor: 1, owner: 2 };

/** Panel etiketleri Türkçedir; API değerleri (viewer/editor/owner) İNGİLİZCE kalır. */
export const ROLE_LABELS: Record<Role, string> = {
  viewer: 'İzleyici',
  editor: 'Editör',
  owner: 'Sahip',
};

/* -------------------------------------------------------------------------- */
/* §42.3 ORG rolleri — PROJE rolleriyle AYRI uzay                              */
/* -------------------------------------------------------------------------- */

/**
 * §42.3 — org rolleri. `Role` ile KARIŞTIRILMAZ ve ortak bir sıralamaları YOKTUR.
 *
 * Panel tarafındaki bu ayna, sunucudaki `ORG_ROLES`/`ORG_ROLE_ORDER` ile AYNI
 * olmak zorundadır: ayrışırlarsa panel, yetkisi olmayan bir düğmeyi gösterir ve
 * kullanıcı 403 ile karşılaşır (ROLE_ORDER için de geçerli olan eski karar).
 */
export type OrgRole = 'member' | 'admin' | 'owner';

export const ORG_ROLE_ORDER: Record<OrgRole, number> = { member: 0, admin: 1, owner: 2 };

/** Panel etiketleri Türkçedir; API değerleri (member/admin/owner) İNGİLİZCE kalır. */
export const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  member: 'Üye',
  admin: 'Yönetici',
  owner: 'Sahip',
};

/** §42.5 — org rolü karşılaştırması; `roleAtLeast`in org uzayındaki karşılığı. */
export function orgRoleAtLeast(role: OrgRole | null | undefined, min: OrgRole): boolean {
  return role !== null && role !== undefined && ORG_ROLE_ORDER[role] >= ORG_ROLE_ORDER[min];
}

/** Rolün ne yapabildiğini tek satırda anlatır — davet formundaki yardım metni. */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  viewer: 'Yalnız okur: tüm panel sayfalarını görür, hiçbir şeyi değiştiremez.',
  editor: 'Okur + bulgu durumu, hedef onayı, SEO taraması ve özet yenileme yapar.',
  owner: 'Tam yetki: ayarlar, e-posta abonelikleri ve üye daveti.',
};

/** Sunucudan gelen serbest string'i güvenle daraltır (bilinmeyen rol = rol yok). */
export function isRole(value: unknown): value is Role {
  return value === 'viewer' || value === 'editor' || value === 'owner';
}

/* -------------------------------------------------------------------------- */
/* Rotalar ve 401 yönlendirmesi (§27.7)                                        */
/* -------------------------------------------------------------------------- */

/** §27.7 — giriş sayfası. */
export const LOGIN_PATH = '/giris';

/** §27.7 — davet kabul sayfası (`BASE_URL/davet?token=…`, §27.4). */
export const INVITE_PATH = '/davet';

/** §43.2 — self-serve kayıt sayfası. Bayrak kapalıyken uç 404 döner (§43.1). */
export const REGISTER_PATH = '/kayit';

/** §43.2 — doğrulama sayfası; mektuptaki linkin hedefi (`BASE_URL/dogrula?token=…`). */
export const VERIFY_PATH = '/dogrula';

/**
 * §43.2/4 — ilk giriş sihirbazı. Guard'ın DIŞINDA DEĞİLDİR: sihirbaz org kurar,
 * org kurmak için OTURUM gerekir (§42.5 `POST /api/orgs` servis belirtecini bile
 * reddeder). Bu yüzden `PUBLIC_PATHS`'e girmez.
 */
export const ONBOARDING_PATH = '/kurulum';

/**
 * §73.1 — hesap sayfası (kimlik · parola · oturumlar). Guard'ın İÇİNDEDİR: hepsi
 * oturumlu kullanıcının kendi kaydına dokunur.
 */
export const ACCOUNT_PATH = '/hesap';

/** §73.3 — parola sıfırlama: talep ve yeni parola sayfaları (guard DIŞI). */
export const FORGOT_PATH = '/parola-sifirla';
export const RESET_PATH = '/parola-yenile';

/**
 * Route guard'ın DIŞINDA kalan yollar.
 *
 * NEDEN kritik: bu sayfalar oturum EDİNMEK içindir; guard'ın arkasına konsalardı
 * `me` 401 → `/giris` → guard → `me` 401 … sonsuz yönlendirme olurdu. Aynı gerekçe
 * sunucudaki muafiyet listesinde de geçerlidir (§27.5: login + accept; §43.2:
 * register/verify/resend de kimliksiz uçlardır).
 */
export const PUBLIC_PATHS: readonly string[] = [
  LOGIN_PATH,
  INVITE_PATH,
  REGISTER_PATH,
  VERIFY_PATH,
  /*
   * §73.3 — parola sıfırlama da OTURUM EDİNME akışıdır: parolasını unutan kullanıcının
   * tanımı gereği oturumu yoktur. Guard'ın arkasında olsalardı bu iki sayfa hiç
   * açılamazdı (login/accept ile birebir aynı gerekçe).
   */
  FORGOT_PATH,
  RESET_PATH,
] as const;

/** Sondaki '/' ve büyük/küçük harf farkı yol eşleşmesini bozmamalı. */
export function isPublicPath(pathname: string): boolean {
  const clean = pathname.toLowerCase().replace(/\/+$/, '');
  const normalized = clean === '' ? '/' : clean;
  return PUBLIC_PATHS.includes(normalized);
}

/**
 * §27.7 — "Herhangi bir API yanıtı 401 gelirse `/giris`'e yönlendir".
 *
 * İki koruma: (1) yalnız 401 — 403 yetki hatasıdır, oturum vardır, kullanıcı sayfadan
 * ATILMAZ, sadece o işlem reddedilir; (2) zaten `/giris` veya `/davet`'teysek
 * yönlendirme yapılmaz — login denemesinin 401'i formu sıfırlamamalıdır.
 */
export function shouldRedirectToLogin(status: number, pathname: string): boolean {
  return status === 401 && !isPublicPath(pathname);
}

/**
 * Girişten sonra dönülecek yol. Açık bir hedef yoksa panelin ana sayfası ('/').
 * `/giris`'in kendisi hedef OLAMAZ (giriş → giriş döngüsü).
 */
export function safeReturnPath(from: unknown): string {
  if (typeof from !== 'string' || from === '') return '/';
  /*
   * Yalnız aynı origin içindeki mutlak yollar. İKİ yazım da dışarı çıkar:
   * '//evil.com' protokol-göreli adrestir; '/\evil.com' ise WHATWG URL ayrıştırmasında
   * AYNI şeye çözülür (özel şemalarda ters bölü, bölü sayılır) — tarayıcı onu
   * 'https://evil.com' olarak okur. Tek bir '/' kontrolü ikincisini kaçırırdı.
   */
  if (!from.startsWith('/') || /^\/[/\\]/.test(from)) return '/';
  const path = from.split('?')[0] ?? from;
  return isPublicPath(path) ? '/' : from;
}

/* -------------------------------------------------------------------------- */
/* Rol kararları (§27.5 / §27.7)                                               */
/* -------------------------------------------------------------------------- */

/** §27.5 — `role >= min` mi? Rol yoksa (oturumsuz) her zaman false. */
export function roleAtLeast(role: Role | null | undefined, min: Role): boolean {
  if (role === null || role === undefined) return false;
  return ROLE_ORDER[role] >= ROLE_ORDER[min];
}

export interface EffectiveRoleInput {
  /** `GET /api/auth/me` kullanıcısı; servis belirtecinde `users` kaydı YOKTUR (§27.6). */
  user: { id: string } | null;
  memberships: readonly { pid: string; role: Role }[];
  pid: string;
  /** `Authorization: Bearer <ADMIN_TOKEN>` ile mi geziliyor (§27.6). */
  serviceToken: boolean;
}

/**
 * §27.6 — aktif projedeki EFEKTİF rol.
 *
 * Sıra sunucunun preHandler sırasıyla aynıdır: önce servis belirteci (rol `owner`,
 * TÜM projeler, `users` kaydı yok), sonra oturumun `memberships` satırı. Kullanıcının
 * o projede üyeliği yoksa `null` döner — panel bunu "salt-okunur/erişim yok" sayar.
 */
export function effectiveRole(input: EffectiveRoleInput): Role | null {
  if (input.serviceToken) return 'owner';
  if (input.user === null) return null;
  const membership = input.memberships.find((m) => m.pid === input.pid);
  return membership !== undefined && isRole(membership.role) ? membership.role : null;
}

/**
 * §27.7 — "davet formu YALNIZ owner'a görünür; viewer/editor listeyi salt-okunur görür".
 * Sunucu tarafındaki karşılığı §27.5'teki `POST /api/auth/invites` → owner satırıdır.
 */
export function canInviteMembers(role: Role | null | undefined): boolean {
  return role === 'owner';
}

/** Üye listesi her role açıktır (§27.5 `GET /api/*` → viewer); oturumsuza kapalı. */
export function canSeeMembers(role: Role | null | undefined): boolean {
  return role !== null && role !== undefined;
}

/* -------------------------------------------------------------------------- */
/* Parola politikası (§27.3)                                                   */
/* -------------------------------------------------------------------------- */

/** §27.3 — en az 10 karakter. */
export const PASSWORD_MIN_LENGTH = 10;

/** §27.3 — bcrypt 72 BAYT sınırı; fazlası sessizce kırpılır, o yüzden reddedilir. */
export const PASSWORD_MAX_BYTES = 72;

/** Karakter değil BAYT sayılır: 'ş' UTF-8'de 2 bayttır, bcrypt sınırı bayt üzerindendir. */
function byteLength(value: string): number {
  try {
    return new TextEncoder().encode(value).length;
  } catch {
    // TextEncoder yoksa (çok eski gömülü webview) karakter sayısı yeterli yaklaşımdır.
    return value.length;
  }
}

/**
 * §27.3 parola politikası: 10..72, en az 1 harf + 1 rakam.
 *
 * Sunucu aynı kontrolü yapar ve politika dışı parolaya 400 `weak_password` döner;
 * buradaki kopya kullanıcıya HATAYI SUNUCUYA GİTMEDEN, hangi kuralın ihlal edildiğini
 * söyleyerek gösterir. Sorun yoksa `null`.
 */
export function passwordProblem(password: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Parola en az ${PASSWORD_MIN_LENGTH} karakter olmalı.`;
  }
  if (byteLength(password) > PASSWORD_MAX_BYTES) {
    return `Parola en çok ${PASSWORD_MAX_BYTES} bayt olabilir (Türkçe harfler 2 bayt sayılır).`;
  }
  // \p{L}: Türkçe harfler de (ş, ğ, İ) "harf" sayılır — ASCII varsayımı yapılmaz.
  if (!/\p{L}/u.test(password)) return 'Parola en az bir harf içermeli.';
  if (!/\d/u.test(password)) return 'Parola en az bir rakam içermeli.';
  return null;
}

/* -------------------------------------------------------------------------- */
/* Sunucu satırlarının normalleştirilmesi                                      */
/* -------------------------------------------------------------------------- */

/** §27.7 üye satırı — "ad, e-posta, rol, son giriş". */
export interface MemberRow {
  userId: string;
  email: string;
  fullName: string | null;
  role: Role;
  /** ISO-8601; hiç giriş yapılmadıysa null (panel '—' yazar, tarih UYDURMAZ). */
  lastLoginAt: string | null;
}

/** §27.2 `invites` satırının panele açılan yüzü — `token` ALANI YOKTUR (tek sırdır). */
export interface InviteRow {
  id: number;
  email: string;
  role: Role;
  /** ISO-8601 — oluşturma + 7 gün (§27.2). */
  expiresAt: string;
  createdAt: string | null;
}

function str(raw: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === 'string' && value !== '') return value;
  }
  return null;
}

/**
 * Üye satırını tek biçime indirger.
 *
 * NEDEN hoşgörülü: §27.4 uç tablosu auth yanıtlarını camelCase yazar (`fullName`),
 * repodaki DB kaynaklı listeler ise satırı snake_case döndürür (`created_at`, §26.6).
 * Üye listesi ucu §27.4 tablosunda YOKTUR (panel için §27.7 gereği eklenmiştir), bu
 * yüzden panel iki yazımı da kabul eder — tek bir alan adı farkı listeyi boş
 * göstermemelidir. Rol tanınmazsa satır 'viewer'a düşer (fail-closed: en az yetki).
 */
export function normalizeMember(input: unknown): MemberRow | null {
  if (input === null || typeof input !== 'object') return null;
  const raw = input as Record<string, unknown>;
  const email = str(raw, 'email');
  if (email === null) return null;
  const role = raw['role'];
  return {
    userId: str(raw, 'userId', 'user_id', 'id') ?? email,
    email,
    fullName: str(raw, 'fullName', 'full_name'),
    role: isRole(role) ? role : 'viewer',
    lastLoginAt: str(raw, 'lastLoginAt', 'last_login_at'),
  };
}

/** Davet satırı — `normalizeMember` ile aynı gerekçe. */
export function normalizeInvite(input: unknown): InviteRow | null {
  if (input === null || typeof input !== 'object') return null;
  const raw = input as Record<string, unknown>;
  const email = str(raw, 'email');
  const expiresAt = str(raw, 'expiresAt', 'expires_at');
  if (email === null || expiresAt === null) return null;
  const id = raw['id'];
  const role = raw['role'];
  return {
    id: typeof id === 'number' ? id : Number(id ?? 0),
    email,
    role: isRole(role) ? role : 'viewer',
    expiresAt,
    createdAt: str(raw, 'createdAt', 'created_at'),
  };
}

/** §27.2 — davetin süresi doldu mu? Liste "süresi doldu" rozetini buna göre basar. */
export function isInviteExpired(invite: InviteRow, now: Date = new Date()): boolean {
  const ts = Date.parse(invite.expiresAt);
  if (Number.isNaN(ts)) return false;
  return ts <= now.getTime();
}
